import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  SeV2QuestionKind,
  SeV2QuestionStatus,
  SourcingEventStatus,
  useGetBuyerSeV2QuestionsQuery,
  useGetEvaluationQuery,
  useListBidV2SForEventQuery,
  useListParticipationRequestsForBuyerQuery,
  type SourcingEventV2Dto,
} from "../../../../autogen/bff-api";
import {
  isSchemeProcedure,
  isTwoStageProcedure,
} from "../../../sourcing-events/buyer/wizard/utils/procedure";
import { useCurrentMoment } from "../../../sourcing-events/portal/utils/useCurrentMoment";
import { useAwardSelection } from "../award/AwardSelectionContext";
import { useRoundSelection } from "../rounds/RoundSelectionContext";
import { panelReadsBids, type NowPanelInput } from "./deriveNowPanel";

const EVALUATING_STATUSES: ReadonlySet<SourcingEventStatus> = new Set([
  SourcingEventStatus.Evaluation,
  SourcingEventStatus.Evaluated,
]);

export const useNowPanelData = (event: SourcingEventV2Dto): NowPanelInput => {
  const eventId = event.id;
  const { rounds } = useRoundSelection();
  const { winnerIds } = useAwardSelection();
  const now = useCurrentMoment().valueOf();

  const procedureType = event.eventContext.type === "Public" ? event.eventContext.procedureType : null;
  const collectsRequests = isTwoStageProcedure(procedureType) || isSchemeProcedure(procedureType);

  const { data: questionsData } = useGetBuyerSeV2QuestionsQuery(
    event.status === SourcingEventStatus.Published ? { eventId } : skipToken,
  );
  const { data: requestsData } = useListParticipationRequestsForBuyerQuery(
    collectsRequests ? { eventId } : skipToken,
  );
  const { data: bidsData, isSuccess: bidsLoaded } = useListBidV2SForEventQuery(
    panelReadsBids(event.status) ? { eventId } : skipToken,
  );
  const { data: evaluation } = useGetEvaluationQuery(
    EVALUATING_STATUSES.has(event.status) ? { eventId } : skipToken,
  );

  const unansweredQuestions = (questionsData?.questions ?? []).filter(
    (question) =>
      question.kind === SeV2QuestionKind.Question &&
      question.status === SeV2QuestionStatus.Unanswered,
  ).length;

  return {
    event,
    now,
    unansweredQuestions,
    participationRequests: requestsData ?? null,
    bids: bidsData?.bids ?? [],
    bidsLoaded,
    selectedWinnerCount: winnerIds.length,
    evaluation: evaluation ?? null,
    rounds,
  };
};
